<template>
  <ModalBase :show-modal="showModal" :auto-h="true" :narrow="true" @close-modal="close">
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal">
        <template v-slot:title>
          <div class="premium-badge--inline">
            <div v-if="isEdit" style="display: flex; align-items: center; gap: 1rem;">
              {{ $gettext('Edit product') }}
              <GkBadge 
                icon="fa-solid fa-crown"
                :text="$gettext('Premium')"
                variant="base"
                type="premium"
              />
            </div>
            <div v-else style="display: flex; align-items: center; gap: 1rem;">
              {{ $gettext('Add new product') }}
              <GkBadge 
                icon="fa-solid fa-crown"
                :text="$gettext('Premium')"
                variant="base"
                type="premium"
              />
            </div>
          </div>
        </template>
      </TitleBar>
    </template>
    <template v-slot:maincontent>
      <b-form id="product-create-form" @submit.stop.prevent>
        <b-form-group id="input-group-1" label-cols-sm="3" content-cols-md="9" label-align-sm="right" label-class="gk-label-margin" :label="$gettext('Description*')" label-for="input-1">
          <b-form-input id="input-1" v-model="item.description" required></b-form-input>
        </b-form-group>
        <b-form-group id="input-group-2" label-cols-sm="3" content-cols-md="6" label-align-sm="right" label-class="gk-label-margin" :label="$gettext('Amount*')" label-for="input-2">
          <b-input-group >
            <b-form-input id="input-2" type="number" v-model="item.amount" required></b-form-input>
            <b-form-select id="input-2" v-model="item.amount_is_gross" :options="priceOptions" required></b-form-select>
          </b-input-group>
        </b-form-group>
        <b-form-group id="input-group-3" label-cols-sm="3" content-cols-md="6" label-align-sm="right" label-class="gk-label-margin" :label="$gettext('Tax rate*')" label-for="input-3">
          <VatInput v-model="item" countryCode="currentCompany.country" /> <!-- TODO incorrect prop format (and not being used on component) -->
        </b-form-group>
        <b-form-group id="input-group-4" label-cols-sm="3" content-cols-md="6" label-align-sm="right" label-class="gk-label-margin" :label="$gettext('Group')" label-for="input-4">
          <b-form-input v-if="showCreateGroupInput" id="input-4" v-model="newGroup.name" required></b-form-input>
          <b-form-select v-else id="input-4" v-model="item.group" :options="groupOptions" value-field="id" text-field="name"></b-form-select>
          <div v-if="allowGroupCreation" class="d-flex justify-content-end">
            <GekkoButton 
              v-if="!showCreateGroupInput"
              :icon="'fa-solid fa-plus'"
              @click="showCreateGroupInput = true"
              :text="$gettext('Create new')"
              variant="link"
              class="text-blue"
            />
            <GekkoButton 
              v-else
              @click="showCreateGroupInput = false"
              :icon="'fa-solid fa-arrow-left'"
              :text="$gettext('Add existing')"
              variant="link"
              class="text-blue"
            />
          </div>
        </b-form-group>
      </b-form>
      <div v-if="isEdit && productInvoices && productInvoices.length > 0" class="usage-box">
        <div class="usage-box--title">
          <h3>{{ $gettext('Used on these invoice drafts') }}</h3>
          <p class="usage-box--hint">{{ $gettext('Updating this product won\'t change current invoices or drafts. Edits must be made manually on each draft.') }}</p>
        </div>

        <ul class="usage-box--list">
          <li v-for="invoice in productInvoices" :key="invoice.id">
            <div>Invoice {{ invoice.number }}</div>
            <div style="display: flex; gap: 0.5rem; align-items: center;">
              <StatusBadge :display-status="getInvoiceStatus(invoice.status)" />
              <GekkoButton
                @click="openEditInvoice(invoice)"
                :text="$gettext('Edit')"
                :icon="'fa-solid fa-pen-to-square'"
                variant="link"
                class="text-blue"
                size="sm"
              />
            </div>
          </li>
        </ul>
      </div>
    </template>
    <template v-slot:actions>
      <div class="d-flex justify-content-between">
        <GekkoButton 
          @click="close()"
          :text="$gettext('Cancel')"
          variant="light"
        />
        <GekkoButton 
          v-if="isEdit"
          @click="updateProduct()"
          :text="$gettext('Save')"
          form="product-create-form"
          type="submit"
          class="position-relative"
          :loading="isWaitingForResponse"
        />
        <GekkoButton
          v-else
          @click="addProduct()"
          :text="$gettext('Add product')"
          form="product-create-form"
          type="submit"
          class="position-relative"
          :loading="isWaitingForResponse"
        />
      </div>
    </template>
  </ModalBase>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";

import ProductGroupService from "@/store/services/productgroup.service.js";
import ProductService from "@/store/services/product.service.js";

import ModalBase from "@/components/base_components/ModalBase";
import TitleBar from "@/components/TitleBar";
import VatInput from "@/components/VatInput.vue";
import GekkoButton from "@/components/GekkoButton";
import StatusBadge from "@/components/invoices/ui/StatusBadge"

export default {
  name: 'AddEditProductModal',
  components: {
    VatInput,
    GekkoButton,
    ModalBase,
    TitleBar,
    StatusBadge
  },
  props: {
    product: {
      type: Object,
      default: () => {}
    },
    initialGroupId: {
      type: Number,
      default: null
    },
    allowGroupCreation: {
      type: Boolean,
      default: false
    },
    productName: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      item: {},
      showModal: true,
      groupOptions: [],
      newGroup: {},
      showCreateGroupInput: false,
      priceOptions: [
        { value: false, text: this.$gettext("Price (ex. VAT)") },
        { value: true, text: this.$gettext("Price (incl. VAT)") }
      ],
      productInvoices: []
    }
  },
  computed: {
    ...mapGetters({
      currentCompany: "main/getCurrentCompany",
      isWaitingForResponse: 'main/isWaitingForResponse'
    }),
    isEdit () {
      return this.product?.id
    }
  },
  created () {
    this.setupData()
  },
  methods: {
    ...mapMutations({
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE'
    }),
    setupData () {
      if (this.initialGroupId) {
        this.group = this.initialGroupId
      }
      this.getProductGroups()
      if (this.product) {
        this.item = Object.assign({}, this.product, {})

        if (this.item.amount_is_gross) {
          this.item.amount = this.item.total_inc_vat
        }
      } else {
        this.item = {
          amount: null,
          description: (this.productName) ? this.productName : null,
          amount_is_gross: false,
          archived: false,
          company_profile: this.currentCompany.id,
          total_inc_vat: false,
          vat_exempt: false,
          group: 0
        }
        if (this.group) {
          this.item.group = this.group
        } else {
          this.item.group = null
        }
        if (!this.currentCompany.default_tax_rate_exempt) {
          this.item.tax_rate = this.currentCompany.default_tax_rate
        } else {
          this.item.tax_rate = 21
          this.item.vat_exempt = true
        }
        if (this.allowGroupCreation) {
          this.newGroup.name = null
        }
      }
      if (this.isEdit) {
        this.getProductUsage()
      }
    },
    getProductUsage () {
      ProductService.getUsage(this.product.id, { invoice_status: 'd' }).then((response) => {
        this.productInvoices = response.data
      })
    },
    getInvoiceStatus (status) {
      let badge_status = { detailed_status: 'draft', display_status: this.$gettext('draft') }
      if (status === 'recurrent') {
        badge_status = { detailed_status: 'recurrent', display_status: this.$gettext('recurrent') }
      }
      return badge_status
    },
    close () {
      this.$emit('close', false)
      document.body.classList.remove('modal-on')
    },
    getProductGroups () {
      return ProductGroupService.list({ company_profile: this.currentCompany.id }).then((response) => {
        this.groupOptions = response.data
        this.groupOptions.unshift({ id: null, name:'---' })
      })
    },
    addProduct () {
      if (!this.currentCompany.is_premium) {
        this.$store.commit('togglePremiumModal', 'web_product_add')
      } else {
        if (this.allowGroupCreation && this.showCreateGroupInput) {
          this.setWaitingForResponse(true)

          ProductGroupService.create({ company_profile: this.currentCompany.id, ...this.newGroup }).then((response) => {
            this.item.group = response.data.id
            this.createProduct(true)
          }).catch((response) => {
            if (response.data['non_field_errors']) {
              this.showAlert('group')
            }
            this.setWaitingForResponse(false)
          })
        } else {
          this.setWaitingForResponse(true)
          if (!this.item.group) {
            delete this.item.group
          }
          this.createProduct()
        }
      }
    },
    updateProduct () {
      this.setWaitingForResponse(true)
      ProductService.update(this.product.id, this.item).then(() => {
        this.$emit('product-updated', false)
        this.close()
      }).catch((response) => {
        if (response.data['non_field_errors']) {
          this.showAlert()
        }
      }).finally(() => {
        this.setWaitingForResponse(false)
      })
    },
    createProduct (with_group = false) {
      ProductService.create(this.item).then((response) => {
        this.$emit('product-created', response.data)
        if (with_group) {
          this.$emit('group-created')
        }
        this.close()
      }).catch((response) => {
        if (with_group) {
          // created product AND group but product did not get through
          // retrieve groups, switch to group select and preselect the new one for this product
          this.getProductGroups().then(() => {
            this.showCreateGroupInput = false
          })
        }
        if (response.data['non_field_errors']) {
          this.showAlert()
        } else if (response.data['amount']) {
          this.showAlert('amount')
        } else {
          this.showAlert('general')
        }
      }).finally(() => {
        this.setWaitingForResponse(false)
      })
    },
    showAlert (type = 'product') {
      let msg = this.$gettext('A product with this name already exists.')
      if (type === 'group') {
        msg = this.$gettext('A group with this name already exists.')
      } else if (type === 'amount') {
        msg = this.$gettext('Please enter a valid amount.')
      } else if (type === 'general') {
        msg = this.$gettext('An error occurred while saving your product. Please check the details and try again.')
      }
      this.$store.commit('setAlert', {
        title: this.$gettext('Could not save'),
        text: msg,
        variant: "warning",
        duration: 2
      })
    },
    openEditInvoice (invoice) {
      // forces a full reload of the current route, otherwise will load gekko view components inside settings
      if (invoice.status === 'd') {
        this.$router.push({ name: 'InvoiceEdit', params: { invoiceId: invoice.id } }).then(() => {
          this.$router.go()
        }).catch(() => {})
      }
      if (invoice.status === 'recurrent') {
        this.$router.push({ name: 'InvoiceViewRecurrent', params: { invoiceId: invoice.recurrentinvoice } }).then(() => {
          this.$router.go()
        }).catch(() => {})
      }
      
    },
  }
}
</script>
<style lang="scss" scoped>
.usage-box {
  padding: 1rem 0;
  border-top: 1px solid $colorBorderDash;

  &--title {
    
  }
  &--hint {
    font-size: 1rem;
    color: $colorTextSemi;
  }
  &--list {
    margin: 0;
    padding: 0;
    font-size: 14px;
    max-height: 300px;
    overflow-y: scroll;

    background: $colorTableOddBg;
    border: 1px solid $colorBorderDash;
    border-radius: $radiusLg;

    li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      &:not(:last-child) {
        border-bottom: 1px solid $colorBorderDash;
      }
      &:hover {
        background: $colorBlue50;
        box-shadow: $tableHoverShadow;
        border-color: transparent;
      }
    }
  }
}
</style>
